"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const react_1 = __importStar(require("react"));
const react_dom_1 = __importDefault(require("react-dom"));
const prop_types_1 = __importDefault(require("prop-types"));
const styled_components_1 = require("styled-components");
const oxygen_button_1 = require("@8x8/oxygen-button");
const react_popper_1 = require("react-popper");
const oxygen_config_1 = require("@8x8/oxygen-config");
const DropdownWrapper_1 = __importStar(require("../styled/DropdownWrapper"));
const DropdownIndicator_1 = __importStar(require("../styled/DropdownIndicator"));
const ScrollWrapper_1 = __importDefault(require("../styled/ScrollWrapper"));
const types_1 = require("../types");
const registerEventHandlers = (handlers) => {
    if (!Array.isArray(handlers)) {
        return;
    }
    for (const handler of handlers) {
        handler.target.addEventListener(handler.eventName, handler.callback, handler.capture);
    }
};
const unregisterEventHandlers = (handlers) => {
    if (!Array.isArray(handlers)) {
        return;
    }
    for (const handler of handlers) {
        handler.target.removeEventListener(handler.eventName, handler.callback, handler.capture);
    }
};
const DropdownButton = ({ theme, customCloseHandlers, forwardedRef, shouldCloseOnInsideClick, isDefaultOpen, indicatorIcon, children, buttonText, idealDirection, isDisabled, onOpen, onClose, invertIconPosition, renderInPortal, ...otherProps }) => {
    const buttonRef = forwardedRef || (0, react_1.useRef)();
    const dropdownRef = (0, react_1.useRef)();
    // keep track of the first render to not call open/close event
    const firstRender = (0, react_1.useRef)(true);
    // local state to keep track of the dropdown open/close state
    const [isOpen, setIsOpen] = (0, react_1.useState)(isDefaultOpen);
    // update the local state when the default (open) state changes
    (0, react_1.useEffect)(() => {
        setIsOpen(isDefaultOpen);
    }, [isDefaultOpen]);
    // call the registred callbacks when the state changes
    (0, react_1.useEffect)(() => {
        // skip the first render
        if (firstRender.current) {
            firstRender.current = false;
            return;
        }
        if (isOpen) {
            onOpen();
        }
        else {
            onClose();
        }
    }, [onOpen, onClose, isOpen]);
    const handleCloseDropdown = ({ target }) => {
        if (buttonRef.current?.contains(target) ||
            buttonRef.current === target ||
            dropdownRef.current?.contains(target) ||
            dropdownRef.current === target) {
            return;
        }
        // eslint-disable-next-line no-use-before-define
        close();
    };
    const closeHandlers = (0, react_1.useCallback)(() => [
        {
            target: window,
            eventName: 'click',
            callback: handleCloseDropdown,
            capture: true,
        },
        {
            target: window,
            eventName: 'contextmenu',
            callback: handleCloseDropdown,
            capture: true,
        },
        ...customCloseHandlers.map(handler => ({
            ...handler,
            callback: handleCloseDropdown,
        })),
    ], [customCloseHandlers]);
    const close = (0, react_1.useCallback)(() => {
        unregisterEventHandlers(closeHandlers());
        setIsOpen(false);
    }, [closeHandlers]);
    if (isDefaultOpen) {
        registerEventHandlers(closeHandlers());
    }
    const open = () => {
        registerEventHandlers(closeHandlers());
        setIsOpen(true);
    };
    const onButtonClick = () => {
        if (isOpen) {
            close();
        }
        else {
            open();
        }
    };
    const onDropdownClick = () => {
        if (!shouldCloseOnInsideClick) {
            return;
        }
        close();
    };
    const setButtonRef = (node) => {
        buttonRef.current = node;
    };
    const setDropdownRef = (node) => {
        dropdownRef.current = node;
    };
    const renderDropdownIndicator = () => {
        const baseProps = {
            left: invertIconPosition,
            isDisabled,
            'aria-hidden': true,
        };
        if (!indicatorIcon) {
            return theme.isClassic ? (react_1.default.createElement(DropdownIndicator_1.default, { ...baseProps })) : (react_1.default.createElement(DropdownIndicator_1.DropdownIcon, { ...baseProps }));
        }
        // @todo proprely mark as deprecated
        if (indicatorIcon) {
            return react_1.default.createElement(DropdownIndicator_1.DropdownIcon, { ...baseProps, as: indicatorIcon });
        }
        return null;
    };
    const renderDropdown = () => (react_1.default.createElement(react_popper_1.Popper, { placement: idealDirection, positionFixed: true, innerRef: setDropdownRef }, ({ ref, style }) => (react_1.default.createElement(DropdownWrapper_1.default, { style: style, ref: ref, onClick: onDropdownClick },
        react_1.default.createElement(ScrollWrapper_1.default, null, children)))));
    return (react_1.default.createElement(styled_components_1.ThemeProvider, { theme: theme },
        react_1.default.createElement(react_popper_1.Manager, null,
            react_1.default.createElement(react_popper_1.Reference, null, ({ ref }) => (react_1.default.createElement(DropdownWrapper_1.StyledButton, { variant: oxygen_button_1.buttonVariant.secondary, isDisabled: isDisabled, 
                // @todo test ref forwarding
                ref: (node) => {
                    ref(node);
                    setButtonRef(node);
                }, onClick: onButtonClick, theme: theme.button, invertItems: invertIconPosition, ...otherProps, "aria-expanded": isOpen, "aria-haspopup": true },
                buttonText,
                renderDropdownIndicator()))),
            isOpen &&
                (renderInPortal
                    ? react_dom_1.default.createPortal(renderDropdown(), document.body)
                    : renderDropdown()))));
};
DropdownButton.propTypes = {
    /**
     * DropdownButton theme
     */
    theme: prop_types_1.default.object.isRequired,
    /**
     * Rendered nodes inside the dropdown
     */
    children: prop_types_1.default.node.isRequired,
    /**
     * Rendered text inside the button
     */
    buttonText: prop_types_1.default.node.isRequired,
    /**
     * Rendered icon inside the button
     * if `undefined` - normal rendering of built-in icon
     * if other `falsey` value it disables the rendering of the indicator icon
     */
    // indicatorIcon: PropTypes.node,
    /**
     * Sets the direction of the flyout
     */
    idealDirection: prop_types_1.default.oneOf([
        'top',
        'right',
        'bottom',
        'left',
        'top-start',
        'top-end',
        'bottom-start',
        'bottom-end',
        'left-start',
        'left-end',
        'right-start',
        'right-end',
    ]),
    /**
     * Tells if the dropdown should close when the content of the dropdown
     * is clicked
     */
    shouldCloseOnInsideClick: prop_types_1.default.bool,
    /**
     * Tell when dropdown is open by default
     */
    isDefaultOpen: prop_types_1.default.bool,
    /**
     * Tell when dropdown button is disabled
     */
    isDisabled: prop_types_1.default.bool,
    /**
     * Callback after dropdown is open
     */
    onOpen: prop_types_1.default.func,
    /**
     * Callback after dropdown is close
     */
    onClose: prop_types_1.default.func,
    /**
     * Array of events that should trigger the closing of the dropdown
     */
    customCloseHandlers: prop_types_1.default.array,
    /**
     * Invert the icon position within the button
     */
    invertIconPosition: prop_types_1.default.bool,
    /**
     * Renders the floating part in a portal
     * This is a temporary item to allow disabling portal rendering.
     * Will likely be removed in Oxygen 3
     * @deprecated
     */
    renderInPortal: prop_types_1.default.bool,
};
DropdownButton.defaultProps = {
    idealDirection: types_1.directions.bottom,
    shouldCloseOnInsideClick: true,
    isDefaultOpen: false,
    isDisabled: false,
    onOpen: () => { },
    onClose: () => { },
    customCloseHandlers: [],
    invertIconPosition: false,
    renderInPortal: true,
};
exports.default = (0, oxygen_config_1.withTheme)(DropdownButton, 'dropdownButton');
